import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { db } from '../firebase/config';
import { encodeEmail } from '../utils/helpers';
import CapsuleCard from '../components/CapsuleCard';
import UnlockingCeremony from '../modals/UnlockingCeremony';
import { UnlockedEnvelopeIcon, SealedEnvelopeIcon, CheckCircleIcon, TrashIcon } from '../components/Icons';

const Dashboard = ({ user, onReply }) => {
  const [viewingState, setViewingState] = useState(null);
  const [mainView, setMainView] = useState('received');
  const [receivedSubView, setReceivedSubView] = useState('ready');
  const [sentSubView, setSentSubView] = useState('ready');
  const [searchTerm, setSearchTerm] = useState('');
  const [friends, setFriends] = useState([]);

  const [lockedReceived, setLockedReceived] = useState([]);
  const [readyToOpenReceived, setReadyToOpenReceived] = useState([]);
  const [openedReceived, setOpenedReceived] = useState([]);
  const [trashedReceived, setTrashedReceived] = useState([]);

  const [lockedSent, setLockedSent] = useState([]);
  const [readySent, setReadySent] = useState([]);
  const [openedSent, setOpenedSent] = useState([]);
  const [trashedSent, setTrashedSent] = useState([]);

  useEffect(() => {
    if (!user) return;

    // Listener for friends list
    const friendsRef = collection(db, 'users', user.uid, 'friends');
    const unsubscribeFriends = onSnapshot(friendsRef, async (snapshot) => {
      const friendsListPromises = snapshot.docs.map(async (friendDoc) => {
        const friendData = { id: friendDoc.id, ...friendDoc.data() };
        // If email is missing, fetch it from the main users collection
        if (!friendData.email) {
          console.log("Debugging db object:", db);
          console.log("Type of db.collection:", typeof db.collection);
          const userDoc = await getDoc(doc(collection(db, 'users'), friendData.id));
          if (userDoc.exists) {
            friendData.email = userDoc.data().email;
          }
        }
        return friendData;
      });
      const friendsList = await Promise.all(friendsListPromises);
      setFriends(friendsList);
    });

    const capsulesRef = collection(db, 'capsules');

    // Sent Capsules
    const sentQuery = query(capsulesRef, where("senderId", "==", user.uid));
    const unsubscribeSent = onSnapshot(sentQuery, (snapshot) => {
      const sentCapsules = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), unlockDate: doc.data().unlockDate.toDate(), createdAt: doc.data().createdAt?.toDate() }));
      const grouped = sentCapsules.reduce((acc, capsule) => {
        const groupId = capsule.groupId || capsule.id;
        if (!acc[groupId]) acc[groupId] = [];
        acc[groupId].push(capsule);
        return acc;
      }, {});
      const sentGroups = Object.values(grouped).sort((a, b) => (b[0].createdAt || 0) - (a[0].createdAt || 0));

      const visibleSent = sentGroups.filter(g => (g[0].senderStatus || 'visible') === 'visible');
      const trashed = sentGroups.filter(g => g[0].senderStatus === 'trashed');

      setLockedSent(visibleSent.filter(g => g[0].status === 'locked'));
      setOpenedSent(visibleSent.filter(g => g.every(c => c.status === 'unlocked')));
      setReadySent(visibleSent.filter(g => g[0].status !== 'locked' && !g.every(c => c.status === 'unlocked')));
      setTrashedSent(trashed);
    });

    // Received Capsules
    const receivedQuery = query(capsulesRef, where("allRecipients", "array-contains", user.email.toLowerCase()));
    const unsubscribeReceived = onSnapshot(receivedQuery, (snapshot) => {
      const receivedCapsules = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), unlockDate: doc.data().unlockDate.toDate(), createdAt: doc.data().createdAt?.toDate() }));
      const encodedUserEmail = encodeEmail(user.email.toLowerCase());

      const getRecipientStatus = (c) => (c.recipientStatuses?.[encodedUserEmail]?.status) || 'visible';

      const visibleReceived = receivedCapsules.filter(c => getRecipientStatus(c) === 'visible');
      const trashed = receivedCapsules.filter(c => getRecipientStatus(c) === 'trashed');

      setLockedReceived(visibleReceived.filter(c => c.status === 'locked').sort((a, b) => a.unlockDate - b.unlockDate));
      setReadyToOpenReceived(visibleReceived.filter(c => c.status === 'ready').sort((a, b) => a.unlockDate - b.unlockDate));
      setOpenedReceived(visibleReceived.filter(c => c.status === 'unlocked').sort((a, b) => b.unlockDate - a.unlockDate));
      setTrashedReceived(trashed.sort((a, b) => b.unlockDate - a.unlockDate));
    });

    return () => {
      unsubscribeSent();
      unsubscribeReceived();
      unsubscribeFriends();
    };
  }, [user]);

  const handleCapsuleOpen = async (capsule) => {
    if (!user) return;
    const capsuleRef = doc(db, 'capsules', capsule.id);
    try {
      if (capsule.disappearOption === 'on_view') {
        const encodedUserEmail = encodeEmail(user.email.toLowerCase());
        await updateDoc(capsuleRef, { [`recipientStatuses.${encodedUserEmail}.status`]: 'deleted', [`recipientStatuses.${encodedUserEmail}.trashedAt`]: serverTimestamp() });
      } else {
        await updateDoc(capsuleRef, { status: 'unlocked' });
      }
    } catch (error) { console.error("Error updating capsule status:", error); }
  };

  const handleSoftDelete = async (capsule, isSender) => {
    if (!user) return;
    const capsuleRef = doc(db, 'capsules', capsule.id);
    try {
      if (isSender) {
        await updateDoc(capsuleRef, { senderStatus: 'trashed', senderTrashedAt: serverTimestamp() });
      } else {
        const encodedUserEmail = encodeEmail(user.email.toLowerCase());
        await updateDoc(capsuleRef, { [`recipientStatuses.${encodedUserEmail}.status`]: 'trashed', [`recipientStatuses.${encodedUserEmail}.trashedAt`]: serverTimestamp() });
      }
    } catch (error) { console.error("Error moving capsule to trash:", error); }
  };

  const handleRestore = async (capsule, isSender) => {
    if (!user) return;
    const capsuleRef = doc(db, 'capsules', capsule.id);
    try {
      if (isSender) {
        await updateDoc(capsuleRef, { senderStatus: 'visible', senderTrashedAt: null });
      } else {
        const encodedUserEmail = encodeEmail(user.email.toLowerCase());
        await updateDoc(capsuleRef, { [`recipientStatuses.${encodedUserEmail}.status`]: 'visible', [`recipientStatuses.${encodedUserEmail}.trashedAt`]: null });
      }
    } catch (error) { console.error("Error restoring capsule:", error); }
  };

  const handleCapsuleClick = (capsule) => {
    if (capsule.status === 'unlocked') setViewingState({ capsule, showCeremony: false });
    else if (capsule.status === 'ready') setViewingState({ capsule, showCeremony: true });
  };

  const handleSentCapsuleClick = (capsule) => setViewingState({ capsule, showCeremony: false });

  if (viewingState) {
    return <UnlockingCeremony capsule={viewingState.capsule} friends={friends} isAlreadyOpened={!viewingState.showCeremony} onClose={() => setViewingState(null)} onMarkAsOpened={() => handleCapsuleOpen(viewingState.capsule)} onReply={onReply} />;
  }

  const filterBySearch = (list) => {
    if (!searchTerm) return list;
    const lowercasedTerm = searchTerm.toLowerCase();
    return list.filter(item => {
      const capsule = Array.isArray(item) ? item[0] : item;
      return capsule.title.toLowerCase().includes(lowercasedTerm) ||
        (capsule.senderEmail && capsule.senderEmail.toLowerCase().includes(lowercasedTerm)) ||
        (capsule.allRecipients && capsule.allRecipients.join(', ').toLowerCase().includes(lowercasedTerm));
    });
  };

  const handleSubViewChange = (view, isSentView = false) => {
    (isSentView ? setSentSubView(view) : setReceivedSubView(view));
    setSearchTerm('');
  };

  const SubNav = ({ isSentView }) => {
    const tabs = isSentView ? [
      { id: 'ready', label: 'Waiting', count: readySent.length, color: 'text-[#4E9A91]', Icon: UnlockedEnvelopeIcon },
      { id: 'locked', label: 'Locked', count: lockedSent.length, color: 'text-[#D4AF37]', Icon: SealedEnvelopeIcon },
      { id: 'opened', label: 'Opened', count: openedSent.length, color: 'text-white', Icon: CheckCircleIcon },
      { id: 'trashed', label: 'Recycling Bin', count: trashedSent.length, color: 'text-red-500', Icon: TrashIcon }
    ] : [
      { id: 'ready', label: 'Ready', count: readyToOpenReceived.length, color: 'text-[#4E9A91]', Icon: UnlockedEnvelopeIcon },
      { id: 'locked', label: 'Locked', count: lockedReceived.length, color: 'text-[#D4AF37]', Icon: SealedEnvelopeIcon },
      { id: 'opened', label: 'Opened', count: openedReceived.length, color: 'text-white', Icon: CheckCircleIcon },
      { id: 'trashed', label: 'Recycling Bin', count: trashedReceived.length, color: 'text-red-500', Icon: TrashIcon }
    ];

    const activeSubView = isSentView ? sentSubView : receivedSubView;

    return (
      <div className="mb-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {tabs.map(({ id, label, count, color, Icon }) => (
            <button
              key={id}
              onClick={() => handleSubViewChange(id, isSentView)}
              className={`flex flex-col items-center justify-center p-4 rounded-lg transition-all duration-300 transform hover:-translate-y-1 ${activeSubView === id ? 'bg-gray-700/50 shadow-lg' : 'bg-gray-800/50 hover:bg-gray-700/50'}`}
            >
              <div className="flex items-center space-x-2">
                <Icon className={`w-5 h-5 ${activeSubView === id ? color : 'text-gray-400'}`} />
                <span className={`font-bold text-sm ${activeSubView === id ? color : 'text-gray-300'}`}>{label}</span>
              </div>
              <span className={`text-2xl font-bold mt-1 ${activeSubView === id ? 'text-white' : 'text-gray-400'}`}>{count}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const SubTabView = () => {
    const isSentView = mainView === 'sent';
    const subView = isSentView ? sentSubView : receivedSubView;
    let listToRender, isTrashedView = false;

    switch (subView) {
      case 'ready': listToRender = isSentView ? readySent : readyToOpenReceived; break;
      case 'locked': listToRender = isSentView ? lockedSent : lockedReceived; break;
      case 'opened': listToRender = isSentView ? openedSent : openedReceived; break;
      case 'trashed': listToRender = isSentView ? trashedSent : trashedReceived; isTrashedView = true; break;
      default: listToRender = [];
    }

    const filtered = filterBySearch(listToRender);
    const CardComponent = (item) => {
      const isGroup = Array.isArray(item);
      const capsule = isGroup ? item[0] : item;
      const key = isGroup ? capsule.groupId || capsule.id : capsule.id;
      return (<CapsuleCard key={key} capsuleGroup={isGroup ? item : [item]} friends={friends} isSender={isSentView} isTrashed={isTrashedView} onClick={() => isSentView ? handleSentCapsuleClick(capsule) : handleCapsuleClick(capsule)} onDelete={() => handleSoftDelete(capsule, isSentView)} onRestore={() => handleRestore(capsule, isSentView)} />);
    };
    return (<div className="space-y-4 pr-2">{filtered.length > 0 ? filtered.map(CardComponent) : <p className="text-[#A0A0A0] text-center py-8">No Kivs in this category.</p>}</div>);
  };

  return (
    <div className="animate-fade-in">
      <main className="animate-fade-in">
        <div className="flex space-x-2 border-b-2 border-gray-800 mb-4">
          <button onClick={() => setMainView('received')} className={`w-full py-3 text-lg font-bold ${mainView === 'received' ? 'text-[#D4AF37] border-b-4 border-[#D4AF37]' : 'text-[#A0A0A0] hover:bg-gray-800'}`}>
            Received Kivs
          </button>
          <button onClick={() => setMainView('sent')} className={`w-full py-3 text-lg font-bold ${mainView === 'sent' ? 'text-[#D4AF37] border-b-4 border-[#D4AF37]' : 'text-[#A0A0A0] hover:bg-gray-800'}`}>
            Sent Kivs
          </button>
        </div>

        <SubNav isSentView={mainView === 'sent'} />

        <SubTabView />
      </main>
    </div>
  );
};

export default Dashboard;
